import React, { useState, useEffect } from "react";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Users as UsersIcon,
  Phone,
  Search,
  Building2,
  Pencil,
  Trash2,
  AlertTriangle,
  LayoutGrid,
  FileCheck2,
  FolderOpen,
  Sparkles,
  Layers,
  Video,
  Image as ImageIcon,
  Flame,
  Save,
} from "lucide-react";
import { toast } from "sonner";

// ── Constants ──────────────────────────────────────────────────────────────
const ALL_SERVICES = ["Digital Marketing", "Ads", "Content Creation"];

const statusConfig = {
  active: {
    label: "Active",
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  on_hold: {
    label: "On Hold",
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
  },
  completed: {
    label: "Completed",
    dot: "bg-slate-400",
    badge: "bg-slate-50 text-slate-600 border-slate-200",
  },
};

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-pink-500",
  "bg-indigo-500",
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function avatarColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++)
    h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const EMPTY_FORM = {
  name: "",
  business: "",
  phone: "",
  services_opted: [],
  status: "active",
};

// ── Skeletons ──────────────────────────────────────────────────────────────
function ClientCardSkeleton() {
  return (
    <Card className="p-5">
      <div className="flex items-start gap-4">
        <Skeleton className="h-12 w-12 rounded-xl shrink-0" />
        <div className="flex-1 space-y-2.5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-48" />
          <div className="flex gap-6 mt-1">
            <Skeleton className="h-3.5 w-24" />
          </div>
          <div className="flex gap-1.5 mt-1">
            <Skeleton className="h-5 w-24 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-28 rounded-full" />
          </div>
        </div>
        <div className="flex gap-1.5">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
      </div>
    </Card>
  );
}

// ── Service Checkboxes ─────────────────────────────────────────────────────
function ServiceCheckboxes({ selected, onChange, label = "Services" }) {
  const toggle = (svc) => {
    if (selected.includes(svc)) onChange(selected.filter((s) => s !== svc));
    else onChange([...selected, svc]);
  };
  return (
    <div>
      {label && (
        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
          {label}
        </Label>
      )}
      <div className="flex flex-wrap gap-2">
        {ALL_SERVICES.map((svc) => {
          const active = selected.includes(svc);
          return (
            <button
              key={svc}
              type="button"
              onClick={() => toggle(svc)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 ${
                active
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              <span
                className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                  active
                    ? "border-white bg-white/20"
                    : "border-muted-foreground/40"
                }`}
              >
                {active && (
                  <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 fill-white">
                    <path
                      d="M1 4l3 3 5-6"
                      stroke="white"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              {svc}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Form Dialog ────────────────────────────────────────────────────────────
function ClientFormDialog({
  open,
  onOpenChange,
  initial,
  onSubmit,
  title,
  submitLabel,
  loading,
}) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);

  useEffect(() => {
    setForm(initial ?? EMPTY_FORM);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.services_opted.length === 0) {
      toast.error("Please select at least one service");
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Fill in client details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Services at top */}
          <ServiceCheckboxes
            selected={form.services_opted}
            onChange={(v) => setForm((f) => ({ ...f, services_opted: v }))}
            label="Services *"
          />

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="Jane Doe"
                value={form.name}
                onChange={set("name")}
                required
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="business">Business Name *</Label>
              <Input
                id="business"
                placeholder="Acme Corp"
                value={form.business}
                onChange={set("business")}
                required
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={set("phone")}
                required
              />
            </div>
          </div>
          <Button type="submit" className="w-full">
            {loading ? "Saving…" : submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirm ─────────────────────────────────────────────────────────
function DeleteConfirmDialog({
  open,
  onOpenChange,
  clientName,
  onConfirm,
  loading,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Delete Client
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{clientName}</strong>? This
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

// ── Client Onboarding & Deliverables Dialog ─────────────────────────────────

function OnboardingDialog({ open, onOpenChange, client }) {
  const [data, setData] = useState({
    brand_references: [""],
    drive_folder_url: "",
    what_they_want: "",
    target_audience: "",
    brand_tone: "",
    deliverables: {
      posts_count: 12,
      posts_format: "Carousel & Single image posts",
      reels_count: 8,
      reels_guidelines: "30-45s vertical format, strong 3s hook",
      stories_count: 30,
      stories_frequency: "Daily stories, interactive polls & stickers",
    },
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && client?.id) {
      setLoading(true);
      apiClient
        .get(`/clients/${client.id}/onboarding`)
        .then((res) => {
          if (res.data) {
            setData({
              brand_references: res.data.brand_references?.length ? res.data.brand_references : [""],
              drive_folder_url: res.data.drive_folder_url || "",
              what_they_want: res.data.what_they_want || "",
              target_audience: res.data.target_audience || "",
              brand_tone: res.data.brand_tone || "",
              deliverables: {
                posts_count: res.data.deliverables?.posts_count ?? 12,
                posts_format: res.data.deliverables?.posts_format || "Carousel & Single image posts",
                reels_count: res.data.deliverables?.reels_count ?? 8,
                reels_guidelines: res.data.deliverables?.reels_guidelines || "30-45s vertical format, strong 3s hook",
                stories_count: res.data.deliverables?.stories_count ?? 30,
                stories_frequency: res.data.deliverables?.stories_frequency || "Daily stories, interactive polls & stickers",
              },
              notes: res.data.notes || "",
            });
          }
        })
        .catch(() => toast.error("Could not load onboarding details"))
        .finally(() => setLoading(false));
    }
  }, [open, client]);

  const handleRefChange = (index, value) => {
    const list = [...data.brand_references];
    list[index] = value;
    setData({ ...data, brand_references: list });
  };

  const addRef = () => {
    setData({ ...data, brand_references: [...data.brand_references, ""] });
  };

  const removeRef = (index) => {
    const list = data.brand_references.filter((_, i) => i !== index);
    setData({ ...data, brand_references: list.length ? list : [""] });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...data,
        brand_references: data.brand_references.filter((r) => r.trim().length > 0),
      };
      await apiClient.post(`/clients/${client.id}/onboarding`, payload);
      toast.success("Onboarding & Deliverables plan updated!");
      onOpenChange(false);
    } catch {
      toast.error("Failed to save onboarding details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <FileCheck2 className="w-5 h-5 text-primary" />
            Client Onboarding & Deliverables — {client?.name}
          </DialogTitle>
          <DialogDescription>
            Reference materials, client expectations, and agreed monthly deliverables.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading onboarding data…</div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6 pt-2">
            {/* 1. References */}
            <div className="space-y-3 p-4 rounded-xl border bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-primary" />
                  1. Brand References & Assets
                </Label>
                <Button type="button" variant="outline" size="sm" onClick={addRef} className="h-7 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Competitor links, aesthetic moodboards, design inspiration</p>
              <div className="space-y-2">
                {data.brand_references.map((ref, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      placeholder="e.g. https://instagram.com/inspiration or Behance link"
                      value={ref}
                      onChange={(e) => handleRefChange(idx, e.target.value)}
                    />
                    {data.brand_references.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeRef(idx)} className="text-muted-foreground hover:text-red-600 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs font-medium">Google Drive Asset Folder</Label>
                <Input
                  className="mt-1"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={data.drive_folder_url}
                  onChange={(e) => setData({ ...data, drive_folder_url: e.target.value })}
                />
              </div>
            </div>

            {/* 2. What they want */}
            <div className="space-y-3 p-4 rounded-xl border bg-muted/20">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                2. Client Requirements & Goals ("What They Want")
              </Label>
              <div>
                <Label className="text-xs">Core Objectives & Desired Outcome</Label>
                <textarea
                  className="w-full mt-1 min-h-[70px] rounded-md border border-input bg-background p-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="What is the client expecting to achieve? Specific themes or USPs to highlight..."
                  value={data.what_they_want}
                  onChange={(e) => setData({ ...data, what_they_want: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Target Audience</Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g. 20-35 year olds, urban entrepreneurs"
                    value={data.target_audience}
                    onChange={(e) => setData({ ...data, target_audience: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Brand Tone & Voice</Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g. Luxury, witty, authoritative, bold"
                    value={data.brand_tone}
                    onChange={(e) => setData({ ...data, brand_tone: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* 3. Deliverables Subsections */}
            <div className="space-y-3 p-4 rounded-xl border bg-muted/20">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Layers className="w-4 h-4 text-primary" />
                3. Monthly Deliverables Quota (CRUD)
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Posts Subsection */}
                <div className="p-3 bg-background rounded-lg border space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-600">
                    <ImageIcon className="w-4 h-4" />
                    Posts
                  </div>
                  <div>
                    <Label className="text-xs">Monthly Count</Label>
                    <Input
                      type="number"
                      className="mt-1"
                      value={data.deliverables.posts_count}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, posts_count: parseInt(e.target.value) || 0 },
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Format / Types</Label>
                    <Input
                      className="mt-1 text-xs"
                      value={data.deliverables.posts_format}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, posts_format: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>

                {/* Reels Subsection */}
                <div className="p-3 bg-background rounded-lg border space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-violet-600">
                    <Video className="w-4 h-4" />
                    Reels / Videos
                  </div>
                  <div>
                    <Label className="text-xs">Monthly Count</Label>
                    <Input
                      type="number"
                      className="mt-1"
                      value={data.deliverables.reels_count}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, reels_count: parseInt(e.target.value) || 0 },
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Hook / Guidelines</Label>
                    <Input
                      className="mt-1 text-xs"
                      value={data.deliverables.reels_guidelines}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, reels_guidelines: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>

                {/* Stories Subsection */}
                <div className="p-3 bg-background rounded-lg border space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-600">
                    <Flame className="w-4 h-4" />
                    Stories
                  </div>
                  <div>
                    <Label className="text-xs">Monthly Count</Label>
                    <Input
                      type="number"
                      className="mt-1"
                      value={data.deliverables.stories_count}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, stories_count: parseInt(e.target.value) || 0 },
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Frequency</Label>
                    <Input
                      className="mt-1 text-xs"
                      value={data.deliverables.stories_frequency}
                      onChange={(e) =>
                        setData({
                          ...data,
                          deliverables: { ...data.deliverables, stories_frequency: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving…" : "Save Onboarding Plan"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [onboardingTarget, setOnboardingTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await apiClient.get("/clients");
      setClients(response.data);
    } catch {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await apiClient.post("/clients", formData);
      toast.success("Client created");
      setCreateOpen(false);
      fetchClients();
    } catch {
      toast.error("Failed to create client");
    }
  };

  const handleEdit = async (formData) => {
    setEditLoading(true);
    try {
      await apiClient.patch(`/clients/${editTarget.id}`, formData);
      toast.success("Client updated");
      setEditTarget(null);
      fetchClients();
    } catch {
      toast.error("Failed to update client");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/clients/${deleteTarget.id}`);
      toast.success("Client deleted");
      setDeleteTarget(null);
      fetchClients();
    } catch {
      toast.error("Failed to delete client");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = clients.filter((c) => {
    const matchSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.business.toLowerCase().includes(search.toLowerCase());
    const matchService =
      serviceFilter.length === 0 ||
      serviceFilter.every((s) => c.services_opted?.includes(s));
    return matchSearch && matchService;
  });

  return (
    <div className="space-y-5 lg:space-y-6" data-testid="clients-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1
            className="text-3xl lg:text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Clients
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {loading
              ? "Loading…"
              : `${clients.length} client${clients.length !== 1 ? "s" : ""} total`}
          </p>
        </div>
        <Button
          data-testid="add-client-button"
          className="w-full sm:w-auto"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* ── Service Filter (above all) ── */}
      <Card className="p-4 border-0 shadow-sm bg-muted/30">
        <ServiceCheckboxes
          selected={serviceFilter}
          onChange={setServiceFilter}
          label="Filter by Service"
        />
        {serviceFilter.length > 0 && (
          <button
            className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setServiceFilter([])}
          >
            Clear filter
          </button>
        )}
      </Card>

      {/* ── Search ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or business…"
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── Client List ── */}
      <div className="grid gap-3 fade-in">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <ClientCardSkeleton key={i} />
          ))
        ) : filtered.length === 0 ? (
          <Card className="py-16 text-center border-dashed">
            <UsersIcon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="font-medium text-muted-foreground">
              {search || serviceFilter.length > 0
                ? "No clients match your filters"
                : "No clients yet"}
            </p>
            {!search && serviceFilter.length === 0 && (
              <p className="text-sm text-muted-foreground/60 mt-1">
                Add your first client to get started
              </p>
            )}
          </Card>
        ) : (
          filtered.map((client) => {
            const sc = statusConfig[client.status] ?? statusConfig.active;
            const initials = getInitials(client.name);
            const color = avatarColor(client.name);
            return (
              <Card
                key={client.id}
                className="p-5 hover-lift"
                data-testid="client-card"
              >
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div
                    className={`w-12 h-12 rounded-xl ${color} text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-sm`}
                  >
                    {initials}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-base">{client.name}</h3>
                      <Badge
                        className={`text-xs border flex items-center gap-1.5 ${sc.badge}`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${sc.dot}`}
                        />
                        {sc.label}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                      <Building2 className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium text-foreground/70 truncate">
                        {client.business}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" />
                        {client.phone}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {client.services_opted?.map((s, i) => (
                        <span
                          key={i}
                          className="text-xs px-2.5 py-0.5 rounded-full bg-primary/8 text-primary/80 border border-primary/15 font-medium"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-1.5 shrink-0 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 text-primary border-primary/20 hover:bg-primary/10"
                      onClick={() => setOnboardingTarget(client)}
                    >
                      <FileCheck2 className="w-3.5 h-3.5 mr-1.5" />
                      Onboarding
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => setEditTarget(client)}
                      data-testid="edit-client-button"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteTarget(client)}
                      data-testid="delete-client-button"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* ── Dialogs ── */}
      <ClientFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={EMPTY_FORM}
        onSubmit={handleCreate}
        title="Add New Client"
        loading={loading}
        submitLabel="Create Client"
      />

      <ClientFormDialog
        open={!!editTarget}
        onOpenChange={(v) => !v && setEditTarget(null)}
        initial={editTarget}
        onSubmit={handleEdit}
        title="Edit Client"
        loading={editLoading}
        submitLabel="Save Changes"
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        clientName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />

      <OnboardingDialog
        open={!!onboardingTarget}
        onOpenChange={(v) => !v && setOnboardingTarget(null)}
        client={onboardingTarget}
      />
    </div>
  );
}
